/**
 * Contribution map deep links: `?contribution=<id>&lat=...&lng=...&zoom=...`
 * URL params are kept for reload/share; sessionStorage backs OAuth redirects.
 */

export const PENDING_CONTRIBUTION_DEEP_LINK_KEY = 'coordal:pending_contribution_deep_link';

export type ContributionDeepLinkCoords = { lat: number; lng: number; zoom: number };

export function readContributionDeepLinkId(): string | null {
  const fromUrl = new URLSearchParams(window.location.search).get('contribution');
  if (fromUrl) return fromUrl;
  return sessionStorage.getItem(PENDING_CONTRIBUTION_DEEP_LINK_KEY);
}

export function stashContributionDeepLinkId(contributionId: string): void {
  sessionStorage.setItem(PENDING_CONTRIBUTION_DEEP_LINK_KEY, contributionId);
}

export function clearContributionDeepLinkId(): void {
  sessionStorage.removeItem(PENDING_CONTRIBUTION_DEEP_LINK_KEY);
}

export function readContributionDeepLinkCoords(): ContributionDeepLinkCoords | null {
  const params = new URLSearchParams(window.location.search);
  const lat = parseFloat(params.get('lat') || '');
  const lng = parseFloat(params.get('lng') || '');
  const zoom = parseInt(params.get('zoom') || '', 10);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return { lat, lng, zoom: Number.isNaN(zoom) ? 17 : zoom };
}

/** Sync contribution deep-link params into the address bar (keeps unrelated query keys). */
export function writeContributionDeepLinkToUrl(
  contributionId: string,
  coords?: ContributionDeepLinkCoords | null,
): void {
  const params = new URLSearchParams(window.location.search);
  params.set('contribution', contributionId);
  if (coords) {
    params.set('lat', String(coords.lat));
    params.set('lng', String(coords.lng));
    params.set('zoom', String(coords.zoom));
  } else {
    params.delete('lat');
    params.delete('lng');
    params.delete('zoom');
  }
  const qs = params.toString();
  window.history.replaceState(
    {},
    '',
    qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
  );
}

/** Remove contribution deep-link params from the address bar (keeps unrelated query keys). */
export function stripContributionDeepLinkParams(): void {
  const params = new URLSearchParams(window.location.search);
  const hadDeepLink =
    params.has('contribution') || params.has('lat') || params.has('lng') || params.has('zoom');
  if (!hadDeepLink) return;

  params.delete('contribution');
  params.delete('lat');
  params.delete('lng');
  params.delete('zoom');
  const qs = params.toString();
  window.history.replaceState(
    {},
    '',
    qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
  );
}
